import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '@/i18n/useAppLocale';
import type { AppLocale } from '@/i18n/types';

interface LanguageSwitcherProps {
  className?: string;
  /**
   * `menu` — profile/settings row with a “Language” label (default).
   * `auth` — compact EN | عربي pill matching CMS login/register.
   */
  variant?: 'menu' | 'auth';
}

export default function LanguageSwitcher({ className, variant = 'menu' }: LanguageSwitcherProps) {
  const { t } = useTranslation('common');
  const { locale, changeLocale } = useAppLocale();

  if (variant === 'auth') {
    const options: { id: AppLocale; label: string }[] = [
      { id: 'en', label: 'EN' },
      { id: 'ar', label: 'عربي' },
    ];
    return (
      <div
        className={clsx(
          'inline-flex rounded-lg border border-gray-200 bg-white/80 p-0.5 text-xs font-medium shadow-sm',
          className,
        )}
        role="group"
        aria-label={t('language')}
      >
        {options.map((option) => {
          const active = locale === option.id;
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={active}
              onClick={() => void changeLocale(option.id)}
              className={clsx(
                'rounded-md px-2.5 py-1 transition-colors',
                active ? 'bg-brand-500 text-white' : 'text-gray-600 hover:text-gray-900',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  }

  const options: { id: AppLocale; label: string }[] = [
    { id: 'en', label: t('english') },
    { id: 'ar', label: t('arabic') },
  ];

  return (
    <div
      className={clsx('flex items-center justify-between gap-3 px-4 py-2', className)}
      role="group"
      aria-label={t('language')}
    >
      <span className="text-xs text-gray-400">{t('language')}</span>
      <div className="inline-flex rounded-md bg-gray-100/80 p-0.5">
        {options.map((option) => {
          const active = locale === option.id;
          return (
            <button
              key={option.id}
              type="button"
              role="menuitemradio"
              aria-checked={active}
              onClick={() => void changeLocale(option.id)}
              className={clsx(
                'rounded px-2 py-0.5 text-xs transition-colors',
                active
                  ? 'bg-white font-medium text-gray-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
